
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { User, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import BaseWidget from './BaseWidget';
import { PatientActivity } from '@/types/dashboard';

interface PatientActivityWidgetProps {
  id: string;
  index: number;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const PatientActivityWidget = ({ 
  id, 
  index, 
  isCollapsed, 
  onToggleCollapse 
}: PatientActivityWidgetProps) => {
  const [activities, setActivities] = useState<PatientActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchRecentActivity();
  }, []);

  const fetchRecentActivity = async () => {
    try {
      // Mock data for now - replace with real Supabase query
      const mockActivities: PatientActivity[] = [
        {
          id: '1',
          patient_name: 'Sarah Wong',
          treatment: 'Botox',
          status: 'completed',
          credits_used: 45,
          time: '2:30 PM'
        },
        {
          id: '2',
          patient_name: 'Amy Patel',
          treatment: 'HydraFacial',
          status: 'no_show',
          credits_used: 0,
          time: '1:00 PM'
        },
        {
          id: '3',
          patient_name: 'Michael Lee',
          treatment: 'Lip Filler',
          status: 'confirmed',
          credits_used: 0,
          time: '3:15 PM'
        }
      ];

      setActivities(mockActivities);
    } catch (error) {
      console.error('Error fetching patient activity:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'no_show': return 'bg-red-100 text-red-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <BaseWidget
      id={id}
      title="👤 Patient Activity Feed"
      index={index}
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
    >
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center text-gray-500">Loading activities...</div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => (
              <div key={activity.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <User className="h-4 w-4 text-juv-terracotta" />
                  <div>
                    <p className="font-medium text-juv-deep-chestnut">{activity.patient_name}</p>
                    <p className="text-sm text-gray-600">{activity.treatment}</p>
                  </div>
                </div>
                <div className="text-right space-y-1">
                  <Badge className={getStatusColor(activity.status)}>
                    {activity.status}
                  </Badge>
                  <p className="text-xs text-gray-500 flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    {activity.time}
                  </p>
                  {activity.credits_used > 0 && (
                    <p className="text-xs text-juv-terracotta">
                      {activity.credits_used} credits
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <Button asChild variant="outline" className="w-full">
          <Link to="/staff/patients">
            View All Patients
          </Link>
        </Button>
      </div>
    </BaseWidget>
  );
};

export default PatientActivityWidget;
